import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect, useDispatch, useSelector } from 'react-redux';
import { useHistory, withRouter } from 'react-router-dom';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { Page, LayoutSingleColumn, IconSpinner } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';
import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { withViewport } from '../../util/uiHelpers';
import { useConfiguration } from '../../context/configurationContext';
import FooterContainer from '../FooterContainer/FooterContainer';
import { convertMarkdownToReact } from '../../util/markdownHelpers';
import { fetchSingleBlog, getBlogSelector, getBlogsInProgress } from '../../ducks/strapi.duck';
import { isArrayLength } from '../../util/genericHelpers';
import { convertStrapiRichTextToReact, extractPlainTextFromStrapiRichText } from '../../util/richTextHelpers';

import css from './SingleBlogPage.module.css';
import { getImagesLink } from '../../util/dataExtractor';
import {
  CalendarOutlined,
} from '@ant-design/icons';
import { Avatar, Button } from 'antd';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

const baseUrl = process.env.REACT_APP_STRAPI_BASE_URL;

export const SingleBlogPageComponent = props => {
  const { scrollingDisabled, params } = props;

  const config = useConfiguration();
  const dispatch = useDispatch();
  const history = useHistory();
  const routeConfiguration = useRouteConfiguration();

  const slug = params?.title;

  useEffect(() => {
    dispatch(fetchSingleBlog({ slug }));
  }, [slug]);

  const blogData = useSelector(getBlogSelector);
  const blogsInProgress = useSelector(getBlogsInProgress);

  const blogImages = blogData?.coverImage?.formats?.url || blogData?.coverImage?.formats?.thumbnail?.url || blogData?.coverImage?.url || blogData?.attributes?.coverImage?.data;
  const title = blogData?.title || blogData?.attributes?.title || '';
  
  // Get the raw description data
  const rawDescription = blogData?.description || blogData?.attributes?.description;
  
  // Extract plain text for meta description
  let descriptionText = '';
  let descriptionContent = null;

  if (Array.isArray(rawDescription)) {
    // It's Strapi rich text format
    descriptionText = extractPlainTextFromStrapiRichText(rawDescription);
    descriptionContent = convertStrapiRichTextToReact(rawDescription);
  } else if (typeof rawDescription === 'string') {
    // It's markdown or plain text
    descriptionText = rawDescription;
    descriptionContent = convertMarkdownToReact(rawDescription);
  }

  const imagesLink = getImagesLink([blogImages], baseUrl);

  const handleClickExploreBlogs = () => {
    const blogsPageLink = createResourceLocatorString(
      'BlogPage',
      routeConfiguration,
      {}
    );
    history.push(blogsPageLink);
  };

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={descriptionText}
      title={title}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: descriptionText,
        name: title,
        image: [facebookImage],
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <div className={css.pageContent}>
          <div className={css.blogsContainer}>
            {blogsInProgress ? (
              <IconSpinner />
            ) : (
              <div className={css.blogsContent}>
                <div className={css.blogsContentBlock}>
                  <h1 className={css.blogsTitle}>{title}</h1>
                  <div className={css.blogDate}>
                    <CalendarOutlined />
                    <span>
                      {blogData?.createdAt ? (
                        new Date(blogData?.createdAt).toDateString()
                      ) : (
                        null
                      )}
                    </span>
                  </div>

                  <div className={css.blogAuthor}>
                    {(blogData?.attributes?.author?.avatar ||
                      blogData?.attributes?.author?.profile_image ||
                      blogData?.author?.avatar ||
                      blogData?.author?.profile_image) ? (
                      <Avatar
                        size={60}
                        src={
                          blogData?.attributes?.author?.avatar ||
                          blogData?.attributes?.author?.profile_image ||
                          blogData?.author?.avatar ||
                          blogData?.author?.profile_image
                        }
                      />
                    ) : (
                      <Avatar size={60}>{blogData?.author?.username?.charAt(0)?.toUpperCase()}</Avatar>
                    )}
                    <em>
                      {
                        blogData?.author?.username?.charAt(0)?.toUpperCase() + blogData?.author?.username?.slice(1) ||
                        blogData?.attributes?.author?.name ||
                        blogData?.attributes?.author_name ||
                        blogData?.author?.name ||
                        blogData?.author_name ||
                        blogData?.attributes?.author?.data?.attributes?.username
                      }
                    </em>
                  </div>
                  {/* {isArrayLength(imagesLink) && <img src={imagesLink[0]} alt={title} className={css.blogsFeaturedimg} />} */}
                  {blogData?.coverImage?.formats?.thumbnail?.url || blogData?.coverImage?.formats?.url || blogData?.coverImage?.url || blogData?.attributes?.coverImage?.data?.attributes?.url ? (
                    <img
                      src={
                        blogData?.coverImage?.formats?.url
                          ? blogData.coverImage.formats.url
                          : blogData?.coverImage?.url
                          ? blogData.coverImage.url
                          : blogData?.coverImage?.formats?.thumbnail?.url
                          ? blogData.coverImage.formats.thumbnail.url
                          : blogData?.attributes?.coverImage?.data?.attributes?.url
                      }
                      alt={title}
                      className={css.blogCoverImage}
                    />
                  ) : (
                    <div className={css.noImagePlaceholder}>No image</div>
                  )}
                </div>

                <div className={css.blogsContentBlock}>
                  {descriptionContent}
                  <div className={css.blogsCTA}>
                    <h2>Interested in our other Blogs?</h2>
                    <p>The latest industry news, interviews, technologies, and resources.</p>
                    <Button onClick={handleClickExploreBlogs} type='primary' size='large'>Explore Blogs</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

SingleBlogPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const SingleBlogPage = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withViewport,
  injectIntl
)(SingleBlogPageComponent);

export default SingleBlogPage;